// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev
import { svgDonut, svgHistogram, svgScatter, type HistBin, type ScatterPt } from '../components/charts';
import { openQuakeDrawer } from '../components/detail';
import { gterm, infoI } from '../components/overlays';
import { depthKm, esc, mag as fmtMag } from '../format';
import { depthCategory, depthColor, depthHistogram, magClass } from '../model';
import { quakes } from '../state';
import { attachSvgZoom } from '../svgZoom';
import { DEPTH_LEGEND, emptyState } from './shared';

export function renderDepth(root: HTMLElement): void {
  const list = quakes();
  if (list.length === 0) { root.innerHTML = emptyState(); return; }

  const edges = [0, 35, 70, 150, 300, 700];
  const dh = depthHistogram(list, edges);
  const histBins: HistBin[] = dh.map((bin) => ({
    label: `${bin.lo}`,
    count: bin.count,
    color: depthColor((bin.lo + bin.hi) / 2),
    tip: `${bin.lo}–${bin.hi} km: ${bin.count} events`,
  }));

  const cats = ['Shallow', 'Intermediate', 'Deep'] as const;
  const catCounts = cats.map((c) => list.filter((q) => depthCategory(q.depth) === c).length);
  const catColors = ['#f87171', '#fbbf24', '#6366f1'];
  const donutSegs = cats.map((c, i) => ({ value: catCounts[i], color: catColors[i], label: c, tip: `${c}: ${catCounts[i]} events` }));

  const maxDepth = Math.max(...list.map((q) => q.depth), 50);
  const minMag = Math.min(...list.map((q) => q.mag));
  const maxMag = Math.max(...list.map((q) => q.mag));
  const scatter: ScatterPt[] = list.map((q) => ({
    x: q.mag,
    y: q.depth,
    r: Math.max(2, 2 + Math.max(0, q.mag) * 0.7),
    color: depthColor(q.depth),
    tip: `M ${fmtMag(q.mag)} ${esc(magClass(q.mag))}\n${esc(q.place)}\n${depthKm(q.depth)} deep`,
    id: q.id,
  }));
  const yTicks = [0, 100, 300, 500, 700].filter((t) => t <= maxDepth + 50);
  const xTicks = tickRange(Math.floor(minMag), Math.ceil(maxMag));

  const shallowPct = Math.round((catCounts[0] / list.length) * 100);

  root.innerHTML = `
    <div class="row" style="align-items:stretch">
      <div class="panel" style="flex:2 1 420px">
        <div class="panel-title">Depth distribution ${infoI('depth')}</div>
        <div class="panel-sub">How far below the surface these earthquakes began, in kilometres. ${gterm('shallow', 'Shallow quakes')} (under 70 km) shake the surface hardest.</div>
        <div class="chart-wrap">${svgHistogram(histBins, { height: 240 })}</div>
      </div>
      <div class="panel" style="flex:1 1 240px">
        <div class="panel-title">Depth bands</div>
        <div class="panel-sub" style="margin:0">Shallow / intermediate / deep</div>
        <div style="display:flex;justify-content:center;margin:0.5rem 0">${svgDonut(donutSegs, 180)}</div>
        <div class="legend">${cats.map((c, i) => `<span class="legend-item"><span class="legend-swatch" style="background:${catColors[i]}"></span>${c} · ${catCounts[i]}</span>`).join('')}</div>
      </div>
    </div>

    <div class="panel" style="margin-top:1rem">
      <div class="panel-title">Magnitude versus depth</div>
      <div class="panel-sub">Every event plotted by size (across) and depth (down). Watch the top-left: the shallowest quakes are the ones most able to do damage at the surface. Scroll to zoom, drag to pan, double-click to reset. Click a point for detail.</div>
      <div class="chart-wrap" style="position:relative" id="scatter-wrap">
        ${svgScatter(scatter, { width: 900, height: 360, domX: [minMag - 0.3, maxMag + 0.3], domY: [maxDepth * 1.02, 0], xLabel: 'Magnitude →', yLabel: '← Depth (km)', yTicks, xTicks })}
      </div>
      <div class="legend">${DEPTH_LEGEND.map((d) => `<span class="legend-item"><span class="legend-swatch" style="background:${d.color}"></span>${d.label}</span>`).join('')}</div>
    </div>

    <div class="callout mt-lg">
      <strong>${shallowPct}% of these quakes are shallow.</strong> Most earthquakes happen in the top
      70 km of the crust. Deep quakes — down to ~700 km, where one plate dives beneath another — are
      felt far less at the surface for the same magnitude, because their energy spreads out before it
      reaches you. ${gterm('shallow', 'Depth bands explained')}.
    </div>`;

  const svg = root.querySelector('#scatter-wrap svg') as SVGSVGElement | null;
  if (svg) attachSvgZoom(svg, { maxScale: 12 });
  root.querySelectorAll('.scatter-dot[data-id]').forEach((d) => d.addEventListener('click', () => {
    const q = list.find((x) => x.id === d.getAttribute('data-id'));
    if (q) openQuakeDrawer(q);
  }));
}

function tickRange(lo: number, hi: number): number[] {
  const out: number[] = [];
  for (let x = lo; x <= hi; x++) out.push(x);
  return out;
}
